import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC, useState } from "react";
import { css } from "styled-components";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ADIColors from "@NA/Utils/ADIColors";
import getColor from "@insite/mobius/utilities/getColor";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { StyledButton } from "@insite/mobius/Clickable";
import { useSelector } from "react-redux";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";

const enum fields {
    direction = "direction",
    alignment = "alignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModel[];
        [fields.customCSS]: string;
    };
    extendedStyles?: LinkListStyles;
    expandByDefault: boolean;
}

export interface LinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    accordionSection?: AccordionSectionPresentationProps;
}

export const linkListStyles: LinkListStyles = {
    accordionSection: {
        toggleIconProps: {
            size: 24,
            css: css`
                margin-right: 20px;
            `,
        },
        headerProps: {
            css: css`
                background: transparent;
                -webkit-tap-highlight-color: transparent;
                -webkit-touch-callout: none;
                -webkit-user-select: none;
                -khtml-user-select: none;
                -moz-user-select: none;
                -ms-user-select: none;
                user-select: none;
                > button {
                    border: 0;
                    padding: 0;
                    background: transparent;
                    margin: 0px;
                    ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                                pointer-events: none;
                            `,
                        css`
                                pointer-events: none;
                            `,
                        css`
                                pointer-events: none;
                            `,
                    ])}
                    :focus {
                        border: 0 !important;
                        outline: 0 !important;
                        padding: 0px;
                    }
                    span[class^="IconWrapper-"] {
                        ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                                    display: none;
                                `,
                        css`
                                    display: none;
                                `,
                        css`
                                    display: none;
                                `,
                    ])}
                        margin-right: 0px!important;
                        > svg {
                            color: ${ADIColors.white};
                            stroke-width: 3px;
                        }
                    }
                }
                > button[aria-expanded="true"],
                button[aria-expanded="false"] {
                    span[class^="IconWrapper-"] {
                        padding: 0px;
                        margin: 0px;
                    }
                }
            `,
        },

        titleTypographyProps: {
            weight: "bold",
            css: css``,
        },

        panelProps: {
            css: css`
                padding: 0px;
                border: none;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0 20px;
                            margin-top: 10px !important;
                        `,
                        css`
                            padding: 0 20px;
                            margin-top: 10px !important;
                        `,
                        css`
                            display: block !important;
                        `,
                        css`
                            display: block !important;
                            margin-top : 12px;
                        `,
                        css`
                            display: block !important;
                            margin-top : 12px;
                        `,
                    ])}
            `,
        },
    },
    linkListWrapper: {
        css: css`
            padding: 10px 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0px;
                        background-color: blue;
                    `,
                    css`
                        padding: 0px;
                        background-color: blue;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    titleWrapper: {
        css: css`
            font-weight: bold;
            > ${StyledButton} {
                > ${TypographyStyle} {
                    font-size: 16px;
                    color: ${getColor("text.main")};
                    font-weight: 700;
                    line-height: 1.5;
                    ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                                    font-size: 18px;
                                    font-weight: 800;
                                    line-height: normal;
                                `,
                    ],
                    "max",
                );
            }}
                }
            }
        `,
    },
    title: {
        css: css`
            font-size: 16px;
            color: ${getColor("text.main")};
            font-weight: 700;
            line-height: 1.5;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 18px;
                            font-weight: 800;
                            line-height: normal;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    linkWrapper: {
        css: css`
            padding: 1px 0px 0 0;
            font-weight: 600;
            > ${StyledButton} {
                &:focus {
                    outline: none;
                    text-decoration: underline;
                    color: ${ADIColors.gray6};
                }
                &:hover {
                    color: ${ADIColors.gray6};
                    > ${TypographyStyle} {
                        color: ${ADIColors.gray6};
                    }
                }
                > ${TypographyStyle} {
                    font-size: 14px;
                    line-height: 24px;
                    margin-top: 12px;
                    color: ${ADIColors.gray6};
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                                margin-top: 10px;
                                line-height:normal;
                            `,
                    css`
                                margin-top: 10px;
                                line-height:normal;
                            `,
                    css`
                                margin-top: 10px;
                                line-height:normal;
                            `,
                    null,
                    null,
                ])}
                    &:hover {
                        color: ${ADIColors.gray6};
                        text-decoration: underline;
                    }
                }
            }
        `,
    },
};

const defaultCustomCss = `.list-wrapper {
}

.title-wrapper {
}

.list-title {
}

.links-wrapper {
}

.list-link {
}
`;

export const LinkList: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);
    const isPendingVerification = useSelector(
        (state: CustomApplicationState) => state.ADIGlobalData.PricingStatusStore.isPendingVerification,
    );
    const titleLink = useGetLink(fields.titleLink);
    const styles = useMergeStyles("linkList", linkListStyles, extendedStyles);
    const [expandByDefault, setExpandByDefault] = useState(false); // Define expand logic

    const alignmentStyles = {
        css: css`
            text-align: ${fields.alignment || "right"};
        `,
    };

    const directionStyles = {
        css: css`
            ${fields.direction === "horizontal" ? "display: inline-block;" : "padding: 1px 0 0 0;"};
            ${styles.linkWrapper?.css}
            font-weight: 600;
        `,
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };   
   // const isPR = window.location.hostname.endsWith(".pr");
    let isPR = false;
    if (typeof window !== "undefined") {
      isPR = window.location.hostname.endsWith(".pr");
    }
    return (
        <StyledWrapper {...alignmentStyles} className="list-wrapper">
            <ManagedAccordionSection
                title={
                    <StyledWrapper {...styles.titleWrapper} className="title-wrapper">
                        {titleLink?.url ? (
                            <Link className="list-title" href={titleLink.url}>{fields.title || titleLink.title}</Link>
                        ) : (
                            <Typography as="h3" {...styles.title} className="list-title">
                                {fields.title}
                            </Typography>
                        )}
                    </StyledWrapper>
                }
                {...styles.accordionSection}
                initialExpanded={fields.title !== "THE COMPANY" ? expandByDefault : true}
            >
                <StyledWrapper className="list-wrapper">
                    {links.map((link, index) => {
                        const linkField = fields.links[index]?.fields;
                        const title = linkField?.overriddenTitle || link.title;
                        const isBranchLocator = title === "Branch Locator";
                        const isMobileOrTab = typeof window !== "undefined" && window.innerWidth <= 1079;
                        let shouldRenderLink = link.url && !(isBranchLocator && isMobileOrTab);
                       

                             if (isPendingVerification) {
                                if (title == "Monthly Sales Flyer") {
                                     return null;
                                   }
                               }
                             if((title == "Monthly Sales Flyer") && (isPR))
                             {shouldRenderLink = false;}
                           
                        return (
                            <StyledWrapper key={index} {...directionStyles} className="links-wrapper">
                                {shouldRenderLink && (
                                    <Link
                                        className="list-link"
                                        href={link.url}
                                        target={linkField?.openInNewWindow ? "_blank" : ""}
                                    >
                                        {title}
                                    </Link>
                                )}
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
            </ManagedAccordionSection>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: LinkList,
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
